import {
  getOutcomeLabel,
  getOutcomeColorClasses,
  OutboundCallOutcome,
  isAcceptedOutcome,
  isAlternativeAccepted,
} from '../../services/outboundCalls';

/**
 * Display component for outbound call outcomes.
 * Shows a modern badge with the outcome status and optional details.
 */
export default function CallOutcomeDisplay({
  outcome,
  outcomeLabel,
  showIcon = true,
  size = 'sm',
  className = '',
}) {
  if (!outcome) return null;

  // Use provided label or fall back to looking it up
  const label = outcomeLabel || getOutcomeLabel(outcome);
  const colorClasses = getOutcomeColorClasses(outcome);
  const icon = getOutcomeIcon(outcome);

  const sizeClasses = {
    xs: 'px-2 py-0.5 text-[10px] gap-1',
    sm: 'px-2.5 py-1 text-xs gap-1.5',
    md: 'px-3 py-1.5 text-sm gap-1.5',
  };

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium
        backdrop-blur-sm transition-all duration-200
        hover:scale-[1.02]
        ${colorClasses}
        ${sizeClasses[size] || sizeClasses.sm}
        ${className}
      `}
    >
      {showIcon && icon}
      <span className="tracking-tight">{label}</span>
    </span>
  );
}

/**
 * Get the appropriate icon for an outcome
 */
function getOutcomeIcon(outcome) {
  const iconClass = 'h-3 w-3 flex-shrink-0';

  switch (outcome) {
    // Success states - checkmark in circle
    case OutboundCallOutcome.CONFIRMED:
    case OutboundCallOutcome.CONFIRMED_ORIGINAL:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        </svg>
      );

    // Rescheduled - calendar clock
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_1:
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_2:
    case OutboundCallOutcome.SELECTED_ALTERNATIVE_3:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
        </svg>
      );

    // Needs reschedule - clock
    case OutboundCallOutcome.NEEDS_RESCHEDULE:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd" />
        </svg>
      );

    // No answer - phone missed
    case OutboundCallOutcome.NO_ANSWER:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path d="M3.5 2A1.5 1.5 0 002 3.5v1.148a.5.5 0 00.095.293l2.232 3.003a.5.5 0 00.405.206h1.775a.5.5 0 01.354.146l1.432 1.433a.5.5 0 01.146.353v1.526a.5.5 0 00.206.405l3.003 2.232a.5.5 0 00.293.095h1.148a1.5 1.5 0 001.5-1.5v-.728a4 4 0 00-.8-2.4l-.8-1.067a1.5 1.5 0 00-1.2-.6h-.934a.5.5 0 01-.354-.146L8.854 6.854a.5.5 0 01-.146-.353v-.934a1.5 1.5 0 00-.6-1.2L7.041 3.567a4 4 0 00-2.4-.8H3.5z" />
          <path d="M13.28 3.22a.75.75 0 10-1.06 1.06L13.94 6l-1.72 1.72a.75.75 0 101.06 1.06L15 7.06l1.72 1.72a.75.75 0 101.06-1.06L16.06 6l1.72-1.72a.75.75 0 00-1.06-1.06L15 4.94l-1.72-1.72z" />
        </svg>
      );

    // Rejected/Failed - X in circle
    case OutboundCallOutcome.REJECTED_ALL_OPTIONS:
    case OutboundCallOutcome.FAILED:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      );

    // Incomplete - question mark
    case OutboundCallOutcome.INCOMPLETE:
    default:
      return (
        <svg className={iconClass} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      );
  }
}

/**
 * Inline outcome display for table cells
 * Shows additional indicator for accepted suggestions
 * Clickable when onClick is provided to show call summary
 */
export function CallOutcomeInline({ outcome, outcomeLabel, onClick, className = '' }) {
  if (!outcome) {
    return <span className={`text-gray-400 text-xs ${className}`}>-</span>;
  }

  const accepted = isAcceptedOutcome(outcome);
  const altAccepted = isAlternativeAccepted(outcome);
  // Use provided label or fall back to looking it up
  const displayLabel = outcomeLabel || getOutcomeLabel(outcome);

  // Build tooltip text based on outcome
  let tooltipText = displayLabel;
  if (altAccepted) {
    tooltipText = 'Patient accepted a different time - appointment rescheduled';
  } else if (accepted) {
    tooltipText = 'Patient confirmed the appointment';
  } else if (outcome === OutboundCallOutcome.NO_ANSWER) {
    tooltipText = 'Patient did not answer - may need follow-up';
  } else if (outcome === OutboundCallOutcome.NEEDS_RESCHEDULE) {
    tooltipText = 'Patient wants to reschedule - action required';
  } else if (outcome === OutboundCallOutcome.REJECTED_ALL_OPTIONS) {
    tooltipText = 'Patient declined all offered times - action required';
  } else if (outcome === OutboundCallOutcome.FAILED) {
    tooltipText = 'Call failed - may need to retry';
  }

  // Add click hint to tooltip if clickable
  if (onClick) {
    tooltipText += ' - Click to view summary';
  }

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <span
      title={tooltipText}
      className={`
        inline-block
        ${onClick ? 'cursor-pointer transition-transform duration-150 hover:scale-105 active:scale-95' : ''}
      `}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && handleClick(e) : undefined}
    >
      <CallOutcomeDisplay outcome={outcome} outcomeLabel={displayLabel} size="xs" className={className} />
    </span>
  );
}

/**
 * Detailed outcome display with timestamp
 */
export function CallOutcomeDetailed({
  outcome,
  timestamp,
  patientResponse,
  className = '',
}) {
  if (!outcome) return null;

  return (
    <div className={`space-y-1 ${className}`}>
      <CallOutcomeDisplay outcome={outcome} size="sm" />
      {timestamp && (
        <p className="text-xs text-gray-500">
          {new Date(timestamp).toLocaleString()}
        </p>
      )}
      {patientResponse && (
        <p className="text-xs text-gray-600 italic">"{patientResponse}"</p>
      )}
    </div>
  );
}
